import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Box, Typography, Button, Chip, IconButton, Accordion, AccordionSummary,
  AccordionDetails, Grid, TextField, Switch, FormControlLabel, Select,
  MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export const newTask = () => ({
  _localId: Date.now() + Math.random(),
  title: '',
  description: '',
  textFieldRequired: false,
  attachmentRequired: false,
  attachmentMin: 1,
  active: true,
  requiredToFinishCalled: false,
});

export default function TaskTemplateEditor({ tasks, onTasksChange, blockFinishWhenRequired = false }) {
  const addTask = () => onTasksChange([...tasks, newTask()]);

  const removeTask = (_localId) =>
    onTasksChange(tasks.filter(t => t._localId !== _localId));

  const updateTask = (_localId, field, value) =>
    onTasksChange(tasks.map(t => t._localId === _localId ? { ...t, [field]: value } : t));

  const handleDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(tasks);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    onTasksChange(reordered);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography fontWeight="bold">
          Tasks
          {tasks.length > 0 && (
            <Chip label={tasks.length} size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: 11 }} />
          )}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addTask}>
          Adicionar task
        </Button>
      </Box>

      {tasks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3, color: '#bbb' }}>
          <Typography variant="body2">
            Nenhuma task adicionada. Clique em "Adicionar task" para começar.
          </Typography>
        </Box>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tasks">
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ bgcolor: snapshot.isDraggingOver ? '#f0f4ff' : 'transparent', borderRadius: 1, transition: 'background 0.15s' }}
              >
                {tasks.map((task, index) => (
                  <Draggable key={String(task._localId)} draggableId={String(task._localId)} index={index}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{ mb: 1, opacity: snapshot.isDragging ? 0.85 : 1 }}
                      >
                        <Accordion
                          variant="outlined"
                          defaultExpanded={!task.title}
                          sx={{
                            '&:before': { display: 'none' },
                            boxShadow: snapshot.isDragging ? '0 4px 16px rgba(0,0,0,0.15)' : 'none',
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, mr: 1, minWidth: 0 }}>
                              <Box
                                {...provided.dragHandleProps}
                                sx={{ display: 'flex', alignItems: 'center', color: '#bbb', flexShrink: 0, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                                onClick={e => e.stopPropagation()}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </Box>

                              <Chip label={index + 1} size="small" color="primary" sx={{ minWidth: 28, height: 22, fontSize: 11, flexShrink: 0 }} />

                              <Typography noWrap sx={{ flexGrow: 1, fontWeight: task.title ? 600 : 400, color: task.title ? 'text.primary' : 'text.disabled', fontSize: 13 }}>
                                {task.title || 'Nova task'}
                              </Typography>

                              {task.textFieldRequired && <Chip label="Texto" size="small" variant="outlined" sx={{ fontSize: 10, height: 20, flexShrink: 0 }} />}
                              {task.attachmentRequired && <Chip label={`Anexo ×${task.attachmentMin}`} size="small" variant="outlined" sx={{ fontSize: 10, height: 20, flexShrink: 0 }} />}
                              {!task.active && <Chip label="Inativo" size="small" sx={{ fontSize: 10, height: 20, flexShrink: 0 }} />}

                              <IconButton
                                size="small"
                                color="error"
                                onClick={e => { e.stopPropagation(); removeTask(task._localId); }}
                                sx={{ flexShrink: 0 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={8}>
                                <TextField
                                  fullWidth
                                  label="Título"
                                  required
                                  inputProps={{ maxLength: 250 }}
                                  value={task.title}
                                  onChange={e => updateTask(task._localId, 'title', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                  control={<Switch checked={task.active} onChange={e => updateTask(task._localId, 'active', e.target.checked)} />}
                                  label="Ativo"
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Descrição"
                                  multiline
                                  rows={2}
                                  value={task.description}
                                  onChange={e => updateTask(task._localId, 'description', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12}><Divider /></Grid>
                              {blockFinishWhenRequired && (
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={task.requiredToFinishCalled || false}
                                        onChange={e => updateTask(task._localId, 'requiredToFinishCalled', e.target.checked)}
                                      />
                                    }
                                    label="Obrigatório para encerrar chamado"
                                  />
                                </Grid>
                              )}
                              <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                  control={<Switch checked={task.textFieldRequired} onChange={e => updateTask(task._localId, 'textFieldRequired', e.target.checked)} />}
                                  label="É obrigatório adicionar descrição?"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                  control={<Switch checked={task.attachmentRequired} onChange={e => updateTask(task._localId, 'attachmentRequired', e.target.checked)} />}
                                  label="Anexo obrigatório?"
                                />
                              </Grid>
                              {task.attachmentRequired && (
                                <Grid item xs={12} sm={4}>
                                  <FormControl fullWidth variant="outlined">
                                    <InputLabel>Mínimo de anexos</InputLabel>
                                    <Select
                                      value={task.attachmentMin}
                                      label="Mínimo de anexos"
                                      onChange={e => updateTask(task._localId, 'attachmentMin', e.target.value)}
                                    >
                                      {[1, 2, 3, 4, 5].map(n => (
                                        <MenuItem key={n} value={n}>{n} anexo{n > 1 ? 's' : ''}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Box>
  );
}

TaskTemplateEditor.propTypes = {
  tasks: PropTypes.array.isRequired,
  onTasksChange: PropTypes.func.isRequired,
  blockFinishWhenRequired: PropTypes.bool,
};
